import React from "react";
import { Code } from "lucide-react";

interface CameraDynamicDetailsProps {
    data: any;
}

const formatKey = (key: string) => {
    return key
        .replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .trim()
        .toLowerCase()
        .replace(/\b\w/g, c => c.toUpperCase());
};

const DynamicDetailsRenderer = ({ data, isRoot = false }: { data: any; isRoot?: boolean }) => {
    if (data === null || data === undefined) {
        return <span className="text-gray-200 font-mono text-[10px]">null</span>;
    }

    if (typeof data !== "object") {
        // Render simple values
        let displayValue = String(data);
        let colorClass = "text-gray-200";

        if (typeof data === "boolean") {
            displayValue = data ? "Yes" : "No";
            colorClass = data ? "text-emerald-400" : "text-rose-400";
        } else if (typeof data === "number") {
            colorClass = "text-blue-400";
        }

        return <span className={`${colorClass} font-mono text-[10px] break-all`}>{displayValue}</span>;
    }

    if (Array.isArray(data)) {
        if (data.length === 0) return <span className="text-gray-200 font-mono text-[10px]">Empty Array</span>;

        // If it's an array of simple strings/numbers, show inline
        if (data.every(item => typeof item !== 'object' && item !== null)) {
            return (
                <div className="flex flex-wrap gap-1 mt-1">
                    {data.map((item, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-gray-900/80 border border-gray-700 text-gray-200 rounded text-[9px] font-mono">
                            {String(item)}
                        </span>
                    ))}
                </div>
            );
        }

        // Object array
        return (
            <div className="mt-1 space-y-1">
                {data.map((item, idx) => (
                    <div key={idx} className="bg-gray-900/40 border border-gray-800/50 rounded pl-2 pr-1 py-1 border-l-2 border-l-orange-500/30">
                        <DynamicDetailsRenderer data={item} />
                    </div>
                ))}
            </div>
        );
    }

    // It's an Object
    const entries = Object.entries(data).filter(([key]) => {
        const k = key.toLowerCase();
        return k !== 'snapshot_uris' && k !== 'snapshoturis';
    });

    if (entries.length === 0) return null;

    if (isRoot) {
        // Use CSS columns for a Masonry layout
        return (
            <div className="columns-1 md:columns-2 lg:columns-3 gap-3 space-y-3">
                {entries.map(([key, val]) => (
                    // break-inside-avoid ensures cards don't split across columns
                    <div key={key} className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden shadow-sm break-inside-avoid w-full">
                        <div className="px-3 py-2 border-b border-gray-700 bg-gray-900/50 flex items-center gap-2">
                            <Code className="w-3.5 h-3.5 text-orange-500" />
                            <h3 className="text-xs font-bold text-gray-200 tracking-wide">{formatKey(key)}</h3>
                        </div>
                        <div className="p-2.5 overflow-x-auto custom-scroll">
                            <DynamicDetailsRenderer data={val} />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-1 w-full">
            {entries.map(([key, val]) => {
                const isComplex = typeof val === 'object' && val !== null;
                return (
                    <div key={key} className="flex flex-col text-[11px]">
                        <div className="flex items-start justify-between py-1 border-b border-gray-700/30">
                            <span className="text-gray-200 font-bold tracking-tight pr-4 shrink-0 uppercase text-[9px] mt-0.5">
                                {formatKey(key)}
                            </span>
                            <div className="flex-1 text-right min-w-0">
                                {isComplex ? (
                                    <div className="text-left mt-1 w-full bg-gray-900/30 rounded p-1.5 border border-gray-800/50">
                                        <DynamicDetailsRenderer data={val} />
                                    </div>
                                ) : (
                                    <DynamicDetailsRenderer data={val} />
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const CameraDynamicDetails: React.FC<CameraDynamicDetailsProps> = ({ data }) => {
    if (!data) return null;

    return (
        <div className="pt-2">
            <div className="flex items-center gap-2 mb-3">
                <Code className="w-4 h-4 text-orange-500" />
                <h2 className="text-sm font-black text-white uppercase tracking-widest">Extended Hardware Telemetry</h2>
                <div className="h-px bg-gray-700 flex-1 ml-2"></div>
            </div>

            {/* The root renderer handles the Masonry columns layout */}
            <DynamicDetailsRenderer data={data} isRoot={true} />
        </div>
    );
};

export default CameraDynamicDetails;
